import { portalText } from "../../i18n/portal.js";
/**
 * Area `agent-document` of the console gateway — owned by plan-003 unit **RC-32** (design §16.8,
 * decision D10 (a), IC-28).
 *
 * What the wizard does with the files a person drops, per file:
 *
 *   1. SHA-256 the bytes in the browser (WebCrypto);
 *   2. `createAgentDocumentUpload` → a write-only link to ONE object, the headers the PUT must carry,
 *      and an opaque signed grant;
 *   3. PUT the bytes straight to the link, reporting progress from the upload's own events — the
 *      API never carries them (its body ceiling is 1 MiB; menus arrive as PDFs and photos);
 *   4. `confirmAgentDocument` with the grant, unchanged — the API re-reads and re-hashes the stored
 *      bytes before the document exists.
 *
 * and then, once every file is confirmed, `startVoiceAgentBuild`. A failed file is retried with a
 * FRESH mint (a new link, a new grant), never by replaying the old one.
 *
 * ── WIRING (AMENDED 2026-09-23, plan-003 Wave 0 integration) ─────────────────────────────
 * The two methods were optional on `ConsoleGateway` while RC-32 could not edit the assembly files.
 * They are now REQUIRED: `api-gateway.ts` spreads {@link agentDocumentApiSlice}, `gateway.ts`
 * registers {@link agentDocumentGap} (empty — all three operations are in the spec), and
 * `sandbox-gateway.ts` implements both in memory.
 */
import { ApiError, assertOk, type WeTalkApiClient } from "@wetalk/api-client";

import { readRecord, readText, readWholeNumber } from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

/** A confirmed document, as the wizard shows it. */
export interface AgentDocumentView {
  readonly agentDocumentId: string;
  readonly filename: string;
  readonly byteSize: number;
}

/** Called with 0…1 as the bytes leave the browser. */
export type UploadProgress = (fraction: number) => void;

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** RC-32. Hash, mint, PUT, confirm — one file. */
    uploadAgentDocument(voiceAgentId: string, file: File, onProgress: UploadProgress): Promise<AgentDocumentView>;
    /** RC-32. Starts the build once every document is confirmed. */
    startVoiceAgentBuild(voiceAgentId: string): Promise<{ readonly buildJobId: string }>;
  }
}

/** Empty: all three operations are in the spec (RC-32). */
export const agentDocumentGap: readonly GatewayGap[] = [];

/** The content types the factory reads, by the extension a browser may fail to type. */
const TYPE_OF_EXTENSION: Readonly<Record<string, string>> = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  txt: "text/plain",
  md: "text/markdown",
  markdown: "text/markdown",
  csv: "text/csv",
  json: "application/json",
};
const ACCEPTED_TYPE: ReadonlySet<string> = new Set(Object.values(TYPE_OF_EXTENSION));

/**
 * The content type to declare — and to PUT with, since the two must match. The browser's own type
 * when it is one the factory reads; otherwise the file's extension decides (browsers commonly type
 * a `.md` file as the empty string). `null` when the factory cannot read it at all.
 */
export function contentTypeOfFile(file: Pick<File, "name" | "type">): string | null {
  if (ACCEPTED_TYPE.has(file.type)) return file.type;
  const dot = file.name.lastIndexOf(".");
  if (dot < 0) return null;
  const extension = file.name.slice(dot + 1).toLowerCase();
  return Object.hasOwn(TYPE_OF_EXTENSION, extension) ? (TYPE_OF_EXTENSION[extension] as string) : null;
}

/** SHA-256 of the file's bytes, lower-case hex, computed in the browser. */
export async function sha256HexOfFile(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

/** The PUT to the upload link. A seam so a suite needs no network and no XHR. */
export type BlobPut = (
  url: string,
  header: Readonly<Record<string, string>>,
  body: Blob,
  onProgress: UploadProgress,
) => Promise<void>;

/** The file could not be put where the link points. `message` is for the person. */
export class DocumentUploadError extends Error {
  override readonly name = "DocumentUploadError";
}

/**
 * The browser's PUT, over XMLHttpRequest because `fetch` reports no upload progress. The link is a
 * signed URL: it is never logged and never put in an error message.
 */
export const xhrBlobPut: BlobPut = (url, header, body, onProgress) =>
  new Promise<void>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("PUT", url);
    for (const [name, value] of Object.entries(header)) request.setRequestHeader(name, value);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total);
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        onProgress(1);
        resolve();
        return;
      }
      reject(new DocumentUploadError(portalText("shell", "upload.storageStatus", { status: request.status })));
    };
    request.onerror = () => {
      reject(new DocumentUploadError(portalText("shell", "upload.connectionFailed")));
    };
    request.send(body);
  });

function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

function headerOf(value: unknown, path: string): Record<string, string> {
  const record = readRecord(value, path);
  const header: Record<string, string> = {};
  for (const [name, one] of Object.entries(record)) header[name] = readText(one, `${path}.${name}`);
  return header;
}

/** What the slice adds — the shape `api-gateway.ts` spreads. */
export interface AgentDocumentGateway {
  uploadAgentDocument(voiceAgentId: string, file: File, onProgress: UploadProgress): Promise<AgentDocumentView>;
  startVoiceAgentBuild(voiceAgentId: string): Promise<{ readonly buildJobId: string }>;
}

export function agentDocumentApiSlice(
  client: WeTalkApiClient,
  _context: SliceContext,
  put: BlobPut = xhrBlobPut,
): AgentDocumentGateway {
  return {
    async uploadAgentDocument(voiceAgentId, file, onProgress): Promise<AgentDocumentView> {
      const contentType = contentTypeOfFile(file);
      if (contentType === null) {
        throw new DocumentUploadError(portalText("shell", "upload.unsupportedType", { filename: file.name }));
      }
      const sha256 = await sha256HexOfFile(file);
      const minted = readRecord(
        readRecord(
          assertOk(
            await client.createAgentDocumentUpload({
              path: { voice_agent_id: voiceAgentId },
              header: { "idempotency-key": newIdempotencyKey() },
              body: { filename: file.name, content_type: contentType, byte_size: file.size, sha256 },
            }),
          ),
          "createAgentDocumentUpload",
        )["data"],
        "createAgentDocumentUpload.data",
      );
      const agentDocumentId = readText(minted["agent_document_id"], "createAgentDocumentUpload.agent_document_id");
      await put(
        readText(minted["upload_url"], "createAgentDocumentUpload.upload_url"),
        headerOf(minted["upload_header"], "createAgentDocumentUpload.upload_header"),
        file,
        onProgress,
      );
      const confirmed = readRecord(
        readRecord(
          assertOk(
            await client.confirmAgentDocument({
              path: { voice_agent_id: voiceAgentId, agent_document_id: agentDocumentId },
              body: { upload_grant: readText(minted["upload_grant"], "createAgentDocumentUpload.upload_grant") },
            }),
          ),
          "confirmAgentDocument",
        )["data"],
        "confirmAgentDocument.data",
      );
      return {
        agentDocumentId: readText(confirmed["agent_document_id"], "confirmAgentDocument.agent_document_id"),
        filename: readText(confirmed["filename"], "confirmAgentDocument.filename"),
        byteSize: readWholeNumber(confirmed["byte_size"], "confirmAgentDocument.byte_size"),
      };
    },

    async startVoiceAgentBuild(voiceAgentId): Promise<{ readonly buildJobId: string }> {
      const result = await client.startVoiceAgentBuild({
        path: { voice_agent_id: voiceAgentId },
        header: { "idempotency-key": newIdempotencyKey() },
        body: {},
      });
      const data = readRecord(readRecord(assertOk(result), "startVoiceAgentBuild")["data"], "startVoiceAgentBuild.data");
      return { buildJobId: readText(data["build_job_id"], "startVoiceAgentBuild.build_job_id") };
    },
  };
}

/** True when the API refused because the build had already started — the goal state already holds. */
export function isBuildAlreadyStarted(raised: unknown): boolean {
  return raised instanceof ApiError && raised.code === "build_already_started";
}
